<template>
  <div>
    <!-- Form Section -->
    <div class="card mb-5">
      <div class="card-header">
        <h3 class="card-title mb-0">Create Correction Item</h3>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="row g-3">
            <div class="col-md-4">
              <label for="company_id" class="form-label">Company *</label>
              <CompanySearchDropdown
                :key="formKey"
                v-model="formCompanyId"
                :withLabel="false"
                :withDescription="false"
                placeholder="Search for a company..."
                @select="handleCompanySelect"
              />
              <div
                class="invalid-feedback d-block"
                v-for="error of v$.formCompanyId.$errors"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
            <div class="col-md-4">
              <label for="description" class="form-label">Description *</label>
              <input
                v-model="formDescription"
                type="text"
                class="form-control"
                id="description"
                :class="{ 'is-invalid': v$.formDescription.$error }"
                placeholder="Reason for correction..."
                @blur="v$.formDescription.$touch"
              />
              <div
                class="invalid-feedback"
                v-for="error of v$.formDescription.$errors"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
            <div class="col-md-2">
              <label for="unit_price" class="form-label"
                >Unit Price (€) *</label
              >
              <input
                v-model.number="formUnitPrice"
                type="number"
                step="0.01"
                min="0"
                class="form-control"
                id="unit_price"
                :class="{ 'is-invalid': v$.formUnitPrice.$error }"
                placeholder="0.00"
                @blur="v$.formUnitPrice.$touch"
              />
              <div
                class="invalid-feedback"
                v-for="error of v$.formUnitPrice.$errors"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
            <div class="col-md-2">
              <label for="amount" class="form-label">Quantity *</label>
              <input
                v-model.number="formAmount"
                type="number"
                min="1"
                class="form-control"
                id="amount"
                :class="{ 'is-invalid': v$.formAmount.$error }"
                placeholder="1"
                @blur="v$.formAmount.$touch"
              />
              <div
                class="invalid-feedback"
                v-for="error of v$.formAmount.$errors"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="row g-3 mt-2">
            <div class="col-md-4">
              <div class="form-check">
                <input
                  v-model="formChargeFee"
                  type="checkbox"
                  class="form-check-input"
                  id="charge_fee"
                />
                <label class="form-check-label" for="charge_fee">
                  Charge Soldera Fee
                </label>
              </div>
            </div>
            <div class="col-md-8 d-flex align-items-end justify-content-end">
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="isSubmitting"
              >
                <span v-if="isSubmitting">
                  <div
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  Creating...
                </span>
                <span v-else>
                  <font-awesome-icon icon="plus" /> Create Correction
                </span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Table Section -->
    <div class="card">
      <div class="card-header">
        <h3 class="card-title mb-0">Existing Corrections</h3>
      </div>
      <div class="card-body">
        <LoadingIndicatorComponent v-if="isLoading" />

        <div v-else-if="corrections.length === 0" class="alert alert-info">
          No correction items found.
        </div>

        <div v-else class="table-responsive">
          <table class="table table-striped">
            <thead>
              <tr>
                <th>Created</th>
                <th>Company</th>
                <th>Company ID</th>
                <th>Description</th>
                <th>Quantity</th>
                <th>Unit Price (€)</th>
                <th>Total Cost (€)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in corrections" :key="item.id">
                <td>{{ formatDateTimeIntoDate(item.created_at) }}</td>
                <td>{{ item.company_name ?? item.company_id }}</td>
                <td>{{ item.company_id }}</td>
                <td>{{ item.description }}</td>
                <td>{{ item.amount }}</td>
                <td>{{ formatMoney(Math.abs(item.unit_price)) }}</td>
                <td>
                  {{ formatMoney(fromUnits(Math.abs(item.net_units))) }}
                </td>
                <td>
                  <span :class="getStatusClass(item)">
                    {{ getStatusText(item) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <ConfirmationModalComponent
      :visible="showConfirmationModal"
      :title="confirmationModalTitle"
      :message="confirmationModalMessage"
      @confirm="handleConfirm"
      @cancel="closeConfirmationModal"
    >
      <template v-if="confirmationModalTitle === 'Confirm Correction Item'">
        <div class="mb-3">
          <p>
            <strong
              >Are you sure you want to create this correction item?</strong
            >
          </p>
          <ul class="list-unstyled">
            <li><strong>Company:</strong> {{ getSelectedCompanyName() }}</li>
            <li><strong>Description:</strong> {{ form.description }}</li>
            <li><strong>Quantity:</strong> {{ form.amount }} MWh</li>
            <li>
              <strong>Unit Price:</strong> €{{ formatMoney(form.unit_price) }}
            </li>
            <li>
              <strong>Total Correction:</strong> €{{
                formatMoney(form.unit_price * form.amount)
              }}
            </li>
          </ul>
          <div class="alert alert-warning">
            <small
              >This will create a correction item that will be included in the
              next expense invoice for this company.</small
            >
            <small v-if="form.charge_fee" class="d-block mt-2">
              <strong
                >⚠️ A fee invoice will also be created using the company's
                standard Soldera commission rate.</strong
              >
            </small>
          </div>
        </div>
      </template>
    </ConfirmationModalComponent>
  </div>
</template>

<script setup lang="ts">
import CompanySearchDropdown from "@/components/common/CompanySearchDropdown.vue";
import ConfirmationModalComponent from "@/components/common/ConfirmationModalComponent.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { formatDateTimeIntoDate, formatMoney } from "@/composables/formatUtils";
import { fromUnits } from "@/composables/money/moneyUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useValidators } from "@/composables/useValidators";
import { InvoiceItem } from "@/types";
import useVuelidate from "@vuelidate/core";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

// Composables
const { t } = useI18n();
const { globalSuccessHandler, globalErrorHandler } = useGlobalHandlers();
const { required, requiredMin, integer } = useValidators(t);

// Reactive state
const corrections = ref<InvoiceItem[]>([]);
const selectedCompany = ref<{ id: number; name: string } | null>(null);
const isLoading = ref(true);
const isSubmitting = ref(false);

// Modal state (following TransactionsListView pattern)
const showConfirmationModal = ref(false);
const confirmationModalTitle = ref("");
const confirmationModalMessage = ref("");
let confirmAction: (() => void) | null = null;

// Form fields
const formCompanyId = ref<number | null>(null);
const formDescription = ref("");
const formUnitPrice = ref(0);
const formAmount = ref(1);
const formChargeFee = ref(false);
const formKey = ref(0); // Key to force component re-render on reset

// Validation rules
const validationRules = {
  formCompanyId: { required },
  formDescription: { required },
  // Unit price is set to negative when submitted to the API
  // Payouts are encoded as negative number in the database, but here we display the positive value
  // Since from a UX perspective, it's more natural to work with positive numbers
  formUnitPrice: { required, min: requiredMin(0.01) },
  formAmount: { required, integer, min: requiredMin(1) },
};

const v$ = useVuelidate(validationRules, {
  formCompanyId,
  formDescription,
  formUnitPrice,
  formAmount,
});

// Form computed helper
const form = computed(() => ({
  company_id: formCompanyId.value,
  description: formDescription.value,
  unit_price: formUnitPrice.value,
  amount: formAmount.value,
  charge_fee: formChargeFee.value,
}));

// Company selection handler
const handleCompanySelect = (company: { id: number; name: string }) => {
  selectedCompany.value = company;
  formCompanyId.value = company.id;
};

// Status calculation functions
const getStatusText = (item: InvoiceItem): string => {
  if (item.expense_paid_at) return "Paid";
  if (item.expense_requested_at) return "Requested";
  return "Pending";
};

const getStatusClass = (item: InvoiceItem): string => {
  if (item.expense_paid_at) return "text-success";
  if (item.expense_requested_at) return "text-warning";
  return "text-secondary";
};

const getSelectedCompanyName = (): string => {
  if (!selectedCompany.value) return "";
  return selectedCompany.value.name;
};

// API functions
const fetchCorrections = async (): Promise<void> => {
  try {
    const response = await goSolidApi.get("/api/admin/correction-items");
    corrections.value = response.data.correction_items;
  } catch (error) {
    console.error("Failed to fetch corrections:", error);
    globalErrorHandler(error);
  }
};

const createCorrection = async (): Promise<void> => {
  try {
    const submitData = {
      company_id: formCompanyId.value,
      description: formDescription.value,
      unit_price: -Math.abs(formUnitPrice.value), // Ensure negative as specified
      amount: formAmount.value,
      charge_fee: formChargeFee.value,
    };

    await goSolidApi.post("/api/admin/register-correction-item", submitData);

    globalSuccessHandler("Success", "Correction item created successfully");

    // Reset form
    formCompanyId.value = null;
    // Commented out to simplify adding multiple corrections
    // formDescription.value = "";
    // formUnitPrice.value = 0;
    // formChargeFee.value = false;
    formAmount.value = 1;
    selectedCompany.value = null;

    // Reset validation
    v$.value.$reset();

    // Force CompanySearchDropdown to re-render by changing key
    formKey.value += 1;

    // Refresh corrections list
    await fetchCorrections();
  } catch (error) {
    console.error("Failed to create correction item:", error);
    globalErrorHandler(error);
  }
};

// Modal management (following TransactionsListView pattern)
const openConfirmationModal = (
  title: string,
  message: string,
  action: () => void
) => {
  confirmationModalTitle.value = title;
  confirmationModalMessage.value = message;
  confirmAction = action;
  showConfirmationModal.value = true;
};

const handleConfirm = () => {
  if (confirmAction) {
    confirmAction();
  }
  closeConfirmationModal();
};

const closeConfirmationModal = () => {
  showConfirmationModal.value = false;
  confirmationModalTitle.value = "";
  confirmationModalMessage.value = "";
  confirmAction = null;
};

// Event handlers
const handleSubmit = async (): Promise<void> => {
  const isValid = await v$.value.$validate();

  if (isValid) {
    openConfirmationModal(
      "Confirm Correction Item",
      "", // Message is empty since we use HTML content in the template slot
      confirmSubmit
    );
  }
};

const confirmSubmit = async (): Promise<void> => {
  isSubmitting.value = true;
  try {
    await createCorrection();
  } finally {
    isSubmitting.value = false;
  }
};

// Lifecycle
onMounted(async () => {
  try {
    await fetchCorrections();
  } catch (error) {
    console.error("Failed to load initial data:", error);
  } finally {
    isLoading.value = false;
  }
});
</script>
